
import * as Sentry from '@sentry/vue';
import { Component, Vue } from 'vue-property-decorator';
import router from './router';
import GlobalLoader from '@/components/UI/GlobalLoader.vue';
import UIVersionAlert from '@/components/UI/versionAlert/UIVersionAlert.vue';
import { Breadcrumb } from '@sentry/vue';
import { BreadcrumbHint, EventHint, Event } from '@sentry/browser';
import { AxiosError, isAxiosError, isCancel } from 'axios';

/**
 * This is a callback that will be called before a breadcrumb is captured
 * This is useful for adding additional data to the breadcrumb
 * For example, you may want to add the request body to the breadcrumb
 * when a request is made
 * https://docs.sentry.io/platforms/javascript/configuration/filtering/?platform=browser#beforebreadcrumb
 */
function handleSentryBeforeBreadcrumb(breadcrumb: Breadcrumb, hint?: BreadcrumbHint) {
  if (breadcrumb.category === 'xhr') {
    // Do not capture body for token requests
    if (breadcrumb.data?.url?.includes('token')) {
      return breadcrumb;
    }
    // Capture response body for requests with status code >= 400
    // else only capture request body
    if (breadcrumb.data?.status_code >= 400) {
      const data = {
        requestBody: hint?.xhr.__sentry_xhr_v2__.body,
        response: hint?.xhr.response,
      };
      return { ...breadcrumb, data };
    } else {
      const data = {
        requestBody: hint?.xhr.__sentry_xhr_v2__.body,
      };
      return { ...breadcrumb, data };
    }
  }
  // Do not capture input events
  // if (breadcrumb.category === 'ui.input') {
  //   return null;
  // }

  // Capture text inside interactive elements
  if (breadcrumb.category === 'ui.click' && hint?.event.target) {
    // const isInteractiveElement = hint.event.target.tabIndex > -1;
    const hasTextInside = Boolean(hint.event.target.innerText);
    if (hasTextInside) {
      breadcrumb.message += `{"${hint.event.target.innerText}"}`;
    }
  }
  return breadcrumb;
}

/**
 * This is a callback that will be called before an event is sent to Sentry
 */
function handleSentryBeforeSend(event: Event, hint: EventHint) {
  // We cancel the request in some cases, so we don't want to capture it in Sentry
  if (isCancel(hint?.originalException)) {
    // console.debug('Cancelled request ignored by Sentry', hint.originalException);
    return null;
  }
  // Most of our fetchers or components functions are throwing Error in the end of the Promise cycle
  // if the error is uknown, it will be caught by Sentry by default, but we don't want to capture 401 errors
  // as they are not critical. That is why we are ignoring them here.
  if (hint?.originalException instanceof AxiosError && hint.originalException.response?.status === 401) {
    // console.debug('401 error ignored by Sentry', hint.originalException);
    return null;
  }
  addAxiosContextRecursive(event, hint?.originalException);
  return event;
}

function addAxiosContextRecursive(event: Event, error: unknown) {
  if (isAxiosError(error)) {
    addAxiosContext(event, error);
  } else if (error instanceof Error && error.cause) {
    addAxiosContextRecursive(event, error.cause);
  }
}

function addAxiosContext(event: Event, error: AxiosError) {
  if (error.response) {
    const contexts = { ...event.contexts };
    contexts.Axios = { Response: error.response };
    event.contexts = contexts;
  }
}

if (process.env.VUE_APP_SENTRY_ON !== 'false') {
  Sentry.init({
    Vue,
    attachProps: true,
    logErrors: true,
    trackComponents: true,
    tracingOptions: {
      trackComponents: true,
    },
    dsn: process.env.VUE_APP_SENTRY_PUBLIC_KEY,
    environment: process.env.VUE_APP_SENTRY_ENVIRONMENT,
    release: 'kkts-portal@' + process.env.VERSION,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.2,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0.2,
    tracePropagationTargets: ['localhost', 'kraken.kkts.com.au/api', 'dashtest.kkts.com.au/api'],
    integrations: [
      new Sentry.Replay({
        maskAllInputs: false,
        maskAllText: false,
        networkDetailAllowUrls: ['kraken.kkts.com.au/api', 'dashtest.kkts.com.au/api'],
        networkDetailDenyUrls: ['kraken.kkts.com.au/api/token', 'dashtest.kkts.com.au/api/token'],
      }),
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],
    beforeSend: handleSentryBeforeSend,
    beforeBreadcrumb: handleSentryBeforeBreadcrumb,
  });
}

@Component({
  components: {
    GlobalLoader,
    UIVersionAlert,
  },
})
export default class App extends Vue {
  get layout(): string {
    return `${this.$route.meta?.layout || 'initial'}-layout`;
  }
}
